import React, { useEffect, useCallback, useRef } from "react";
import { observer } from "mobx-react-lite";
import Icon from "@mdi/react";
import { mdiEyeOutline } from "@mdi/js";
import { Chip, SearchField, Table, cn } from "@heroui/react";
import { contractorDetailStore } from "@/stores/ContractorDetailStore";
import {
  TRANSACTION_TYPE_LABEL,
  TRANSACTION_TYPE_COLOR,
  TRANSACTION_STATUS_LABEL,
  TRANSACTION_STATUS_COLOR,
  PAYMENT_METHOD_LABEL,
} from "../constants";
import { SortPanel, SortableColumnHeader } from "./shared";
import { CombinedFilter } from "@/shared/ui/CombinedFilter";
import { LoadMoreSentinel } from "@/shared/ui/LoadMoreSentinel";
import { ChipWithTooltip } from "@/shared/ui/ChipWithTooltip";
import { useSortPanel } from "@/hooks/useSortPanel";
import { formatMoneyToRub } from "@shared-utils/helpers";

const TX_SORT_COLUMNS = [
  { id: "date", label: "Дата" },
  { id: "amount", label: "Сумма" },
];

const TransactionsTab: React.FC = observer(() => {
  const store = contractorDetailStore;
  const sort = useSortPanel("date");

  const handleLoadMore = useCallback(() => {
    store.tx.loadMore();
  }, [store]);

  useEffect(() => {
    store.tx.load();
  }, []);

  // Синхронизация sort → store + перезагрузка
  useEffect(() => {
    const prefix = sort.sortDescriptor.direction === "descending" ? "-" : "";
    const newSort = `${prefix}${sort.sortDescriptor.column}`;
    if (newSort !== store.tx.sort) {
      store.tx.setSort(newSort);
      // Клиентская сортировка для транзакций — не перезагружаем
    }
  }, [sort.sortDescriptor]);

  // Клиентская сортировка
  const sorted = [...store.tx.items].sort((a, b) => {
    let cmp = 0;
    if (sort.sortDescriptor.column === "date") {
      cmp = a.date.localeCompare(b.date);
    } else if (sort.sortDescriptor.column === "amount") {
      cmp = a.amount - b.amount;
    }
    return sort.sortDescriptor.direction === "descending" ? -cmp : cmp;
  });

  const searchTimer = useRef<number>(0);

  const handleSearch = (val: string) => {
    store.tx.setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = window.setTimeout(() => store.tx.load(1), 500);
  };

  const handleTypeFilter = (val: string[]) => {
    store.tx.setTypeFilter(val);
    store.tx.load(1);
  };

  const handleStatusFilter = (val: string[]) => {
    store.tx.setStatusFilter(val);
    store.tx.load(1);
  };

  return (
    <div className="scd-tab-content">
      <div className="scd-toolbar">
        <div className="scd-toolbar__left">
          <SearchField
            value={store.tx.search}
            onChange={handleSearch}
            aria-label="Поиск операций"
            className="scd-search"
          >
            <SearchField.Group>
              <SearchField.SearchIcon />
              <SearchField.Input placeholder="Поиск..." />
              <SearchField.ClearButton />
            </SearchField.Group>
          </SearchField>

          <CombinedFilter
            groups={[
              {
                id: "type",
                label: "Тип",
                options: [
                  { id: "payment", label: "Оплата" },
                  { id: "payout", label: "Выплата" },
                  { id: "refund", label: "Возврат" },
                ],
                value: store.tx.typeFilter,
                onChange: handleTypeFilter,
              },
              {
                id: "status",
                label: "Статус",
                options: [
                  { id: "success", label: "Проведён" },
                  { id: "pending", label: "Ожидание" },
                  { id: "processing", label: "Обработка" },
                  { id: "refunded", label: "Возвращён" },
                  { id: "partially_refunded", label: "Частичный возврат" },
                  { id: "failed", label: "Ошибка" },
                ],
                value: store.tx.statusFilter,
                onChange: handleStatusFilter,
              },
            ]}
          />
        </div>
        <div className="scd-toolbar__right">
          <SortPanel
            columns={TX_SORT_COLUMNS}
            sortDescriptor={sort.sortDescriptor}
            setSortDescriptor={sort.setSortDescriptor}
            isSortOpen={sort.isSortOpen}
            setIsSortOpen={sort.setIsSortOpen}
            sortAnchorRef={sort.sortAnchorRef}
            sortPanelRef={sort.sortPanelRef}
          />
        </div>
      </div>

      <div className="scd-table-wrap">
        <Table variant="secondary">
          <Table.ScrollContainer className="scd-scroll-container">
            <Table.Content
              aria-label="История операций"
              className="scd-table scd-table--transactions"
              sortDescriptor={sort.sortDescriptor}
              onSortChange={(d) =>
                sort.setSortDescriptor({
                  column: String(d.column),
                  direction: d.direction as "ascending" | "descending",
                })
              }
            >
              <Table.Header className="scd-table__head scd-table__head--sticky">
                <Table.Column
                  id="date"
                  className="scd-table__th"
                  isRowHeader
                  allowsSorting
                >
                  {({ sortDirection }) => (
                    <SortableColumnHeader sortDirection={sortDirection}>
                      Дата
                    </SortableColumnHeader>
                  )}
                </Table.Column>
                <Table.Column id="type" className="scd-table__th">
                  Тип
                </Table.Column>
                <Table.Column id="description" className="scd-table__th">
                  Описание
                </Table.Column>
                <Table.Column id="method" className="scd-table__th">
                  Метод
                </Table.Column>
                <Table.Column
                  id="amount"
                  className="scd-table__th scd-table__th_right"
                  allowsSorting
                >
                  {({ sortDirection }) => (
                    <SortableColumnHeader sortDirection={sortDirection}>
                      Сумма
                    </SortableColumnHeader>
                  )}
                </Table.Column>
                <Table.Column id="status" className="scd-table__th">
                  Статус
                </Table.Column>
                <Table.Column
                  id="actions"
                  className="scd-table__th scd-table__th--actions"
                >
                  <span className="sr-only">Действия</span>
                </Table.Column>
              </Table.Header>
              <Table.Body className="scd-table__body">
                {sorted.length === 0 ? (
                  <Table.Row className="scd-table__row">
                    <Table.Cell className="scd-table__td" colSpan={7}>
                      <div className="scd-table-empty">
                        {store.tx.isLoading ? "Загрузка..." : "Нет данных"}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  sorted.map((t) => (
                    <Table.Row
                      key={t.uuid}
                      id={t.uuid}
                      data-uuid={t.uuid}
                      className="scd-table__row"
                    >
                      <Table.Cell className="scd-table__td scd-table__td--date">
                        {new Date(t.date).toLocaleString("ru-RU", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Table.Cell>
                      <Table.Cell className="scd-table__td">
                        <Chip
                          color={TRANSACTION_TYPE_COLOR[t.type] ?? "default"}
                          variant="soft"
                          size="sm"
                        >
                          {TRANSACTION_TYPE_LABEL[t.type] ?? t.type}
                        </Chip>
                      </Table.Cell>
                      <Table.Cell className="scd-table__td">
                        <span className="scd-td-truncate" title={t.description}>
                          {t.description}
                        </span>
                      </Table.Cell>
                      <Table.Cell className="scd-table__td">
                        <span
                          className="scd-td-truncate"
                          title={
                            t.paymentMethod
                              ? (PAYMENT_METHOD_LABEL[t.paymentMethod] ??
                                t.paymentMethod)
                              : undefined
                          }
                        >
                          {t.paymentMethod
                            ? (PAYMENT_METHOD_LABEL[t.paymentMethod] ??
                              t.paymentMethod)
                            : "—"}
                        </span>
                      </Table.Cell>
                      <Table.Cell
                        className={cn(
                          "scd-table__td scd-table__td_right",
                          t.amount >= 0
                            ? "scd-amount--positive"
                            : "scd-amount--negative",
                        )}
                      >
                        {t.amount >= 0 ? "+" : ""}
                        {formatMoneyToRub(t.amount)}
                      </Table.Cell>
                      <Table.Cell className="scd-table__td">
                        {(() => {
                          const statusLabel =
                            t.statusDisplay ||
                            TRANSACTION_STATUS_LABEL[t.status] ||
                            t.status;
                          return (
                            <ChipWithTooltip
                              label={statusLabel}
                              color={
                                TRANSACTION_STATUS_COLOR[t.status] ?? "default"
                              }
                            />
                          );
                        })()}
                      </Table.Cell>
                      <Table.Cell className="scd-table__td scd-table__td--actions">
                        <button
                          type="button"
                          className="scd-eye-btn"
                          aria-label="Просмотр"
                          onClick={() =>
                            store.pd.open(t.uuid, t.description, t.type)
                          }
                        >
                          <Icon path={mdiEyeOutline} size={0.8} />
                        </button>
                      </Table.Cell>
                    </Table.Row>
                  ))
                )}
              </Table.Body>
            </Table.Content>
          </Table.ScrollContainer>
        </Table>
        <LoadMoreSentinel
          onLoadMore={handleLoadMore}
          isLoading={store.tx.isLoading}
          hasMore={store.tx.meta.hasNextPage}
        />
      </div>
    </div>
  );
});

TransactionsTab.displayName = "TransactionsTab";
export default TransactionsTab;
